import React, { useState, useEffect } from 'react';
import { X, Image, Link, FileText, Save } from 'lucide-react';
import ImageUploader from './ImageUploader';

import { UsefulLink } from '../lib/supabase';

interface UsefulLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (linkData: Omit<UsefulLink, 'id' | 'createdAt' | 'updatedAt'>) => void;
  editingLink: UsefulLink | null;
}

const UsefulLinkModal: React.FC<UsefulLinkModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingLink
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    image: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Preencher formulário quando estiver editando
  useEffect(() => {
    if (editingLink) {
      setFormData({
        title: editingLink.title,
        description: editingLink.description,
        url: editingLink.url,
        image: editingLink.image
      });
    } else {
      setFormData({
        title: '',
        description: '',
        url: '',
        image: ''
      });
    }
    setErrors({});
  }, [editingLink, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'O título é obrigatório';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'A descrição é obrigatória';
    }

    if (!formData.url.trim()) {
      newErrors.url = 'A URL é obrigatória';
    } else if (!isValidUrl(formData.url)) {
      newErrors.url = 'Digite uma URL válida (ex: https://exemplo.com)';
    }

    if (!formData.image.trim()) {
      newErrors.image = 'A URL da imagem é obrigatória';
    } else if (!isValidUrl(formData.image)) {
      newErrors.image = 'Digite uma URL válida para a imagem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const normalizeGitHubUrl = (url: string) => {
    try {
      let u = url.trim();
      u = u.replace(/\?.*$/, '');
      if (u.includes('github.com')) {
        u = u
          .replace('https://github.com/', 'https://raw.githubusercontent.com/')
          .replace('/blob/', '/');
      }
      u = u.replace('/refs/heads/', '/');
      return u;
    } catch {
      return url;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave({ ...formData, image: normalizeGitHubUrl(formData.image) });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1d21] rounded-2xl border border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <h2 className="text-2xl font-bold text-white">
            {editingLink ? 'Editar Link' : 'Adicionar Novo Link'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Título */}
          <div>
            <label className="flex items-center space-x-2 text-white font-medium mb-2">
              <FileText size={18} />
              <span>Título do Link *</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Ex: Ferramenta de Análise de Mercado"
              className={`w-full bg-[#121316] text-white px-4 py-3 rounded-xl border transition-colors ${
                errors.title 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-600 focus:border-[#0FFF55]'
              } focus:outline-none`}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title}</p>
            )}
          </div>

          {/* Descrição */}
          <div>
            <label className="flex items-center space-x-2 text-white font-medium mb-2">
              <FileText size={18} />
              <span>Descrição *</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Descreva brevemente o que este link oferece..."
              rows={3}
              className={`w-full bg-[#121316] text-white px-4 py-3 rounded-xl border transition-colors resize-none ${
                errors.description 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-600 focus:border-[#0FFF55]'
              } focus:outline-none`}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          {/* URL */}
          <div>
            <label className="flex items-center space-x-2 text-white font-medium mb-2">
              <Link size={18} />
              <span>URL do Link *</span>
            </label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              placeholder="https://exemplo.com"
              className={`w-full bg-[#121316] text-white px-4 py-3 rounded-xl border transition-colors ${
                errors.url 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-600 focus:border-[#0FFF55]'
              } focus:outline-none`}
            />
            {errors.url && (
              <p className="text-red-500 text-sm mt-1">{errors.url}</p>
            )}
          </div>

          {/* URL da Imagem / Upload */}
          <div>
            <ImageUploader
              bucketName="links_uteis"
              label="Imagem do Link *"
              value={formData.image}
              onChange={(url) => handleInputChange('image', url)}
              options={{ maxWidth: 800, maxHeight: 600, quality: 0.85, format: 'webp' }}
              placeholder="https://exemplo.com/imagem.jpg"
            />
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">{errors.image}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 bg-[#0FFF55] text-[#121316] px-6 py-3 rounded-xl hover:bg-[#0dd149] transition-colors font-semibold"
            >
              <Save size={18} />
              <span>{editingLink ? 'Salvar Alterações' : 'Criar Link'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsefulLinkModal; 